import { useMemo } from 'react'
import React from 'react'
import { useHistory } from 'react-router-dom'
import { WemoloTextLink } from '../../components/Wemolo'
import { useGetStationNames } from '../StationView/utils/station-hooks'
import { useDataFetcher } from '../../utils'
import { useGlobalSearchPageStore } from './GlobalSearchPage'
import { SearchGlobalQuerySchema, type SearchGlobalQuery } from './globalSearch_schemas'

export type { SearchGlobalQuery }

export interface SearchItem {
    type: 'lot' | 'station' | 'device'
    id: string
    name: string
    description: string | React.ReactNode
    searchableDescription: string
    deviceType?: string | null
    navigateTo: () => void
}

export const useGlobalSearchData = (searchTerm: string) => {
    const history = useHistory()
    const setIsOpen = useGlobalSearchPageStore((state) => state.setIsOpen)

    // Keep stations in memory for relationship lookups (not 30k records)
    const { data: allStations } = useGetStationNames()

    // For multi-term searches, use the FIRST term for server search (usually device type/main ID).
    // This ensures we get broad enough results from the server; client-side refinement then applies
    // precise AND logic for all terms.
    const firstTerm = searchTerm?.trim().split(/\s+/).filter(Boolean)[0]
    const enabled = !!firstTerm && firstTerm.length >= 2

    const { data, isFetching: loading } = useDataFetcher(
        'search_global',
        { searchTerm: enabled ? `%${firstTerm}%` : '', limit: 100 },
        SearchGlobalQuerySchema,
        {
            queryKey: ['data_fetcher', 'search_global', firstTerm],
            enabled,
            staleTime: 0,
            cacheTime: 0
        }
    )

    // Transform raw query results into SearchItems
    const rawSearchData = useMemo(() => {
        if (!data) return []

        const lotItems =
            data.lot?.map((lot) => {
                // Include ID, name, and address in searchable description for multi-term matching
                const searchableFields = [lot.id, lot.name, lot.address].filter(Boolean).join(' ')
                return {
                    type: 'lot' as const,
                    id: lot.id,
                    name: lot.id,
                    description: lot.name || '',
                    searchableDescription: searchableFields,
                    navigateTo: () => {
                        history.push(`/lot?lot=${lot.id}`)
                        setIsOpen(false)
                    }
                }
            }) || []

        const stationItems =
            data.station?.map((station) => {
                const searchableDescription = `Lot: ${station.station_name.split('_')[0]}${
                    station.device_uuid ? ` • Device: ${station.device_uuid}` : ''
                }`
                return {
                    type: 'station' as const,
                    id: station.station_name,
                    name: station.station_name,
                    searchableDescription: searchableDescription,
                    description: (
                        <span>
                            Lot: {station.station_name.split('_')[0]}
                            {station.device_uuid && (
                                <>
                                    {' • '}
                                    <WemoloTextLink
                                        size="small"
                                        href={`/device?deviceId=${station.device_uuid}`}
                                        onClick={(e) => {
                                            e.preventDefault()
                                            e.stopPropagation()
                                            history.push(`/device?deviceId=${station.device_uuid}`)
                                            setIsOpen(false)
                                        }}
                                    >
                                        Device: {station.device_uuid}
                                    </WemoloTextLink>
                                </>
                            )}
                        </span>
                    ),
                    navigateTo: () => {
                        history.push(`/station?stationName=${station.station_name}`)
                        setIsOpen(false)
                    }
                }
            }) || []

        const deviceItems =
            data.device?.map((device) => {
                const linkedStations = allStations?.station?.filter((s) => s.device_uuid === device.uuid) || []
                const linkedLots = [...new Set(linkedStations.map((s) => s.station_name.split('_')[0]))]

                const searchableDescription =
                    linkedLots.length > 0
                        ? `Lot: ${linkedLots.join(', ')} • Type: ${device.type} • ${
                              device.uuid
                          } • Stations: ${linkedStations.map((s) => s.station_name).join(', ')}`
                        : `Type: ${device.type} • ${device.uuid}`

                const description = (
                    <span>
                        {linkedLots.length > 0 && `Lot: ${linkedLots.join(', ')} • `}
                        Type: {device.type} • {device.uuid}
                        {linkedStations.length > 0 && (
                            <>
                                {' • Stations: '}
                                {linkedStations.map((station, idx) => (
                                    <React.Fragment key={station.station_name}>
                                        {idx > 0 && ', '}
                                        <WemoloTextLink
                                            size="small"
                                            href={`/station?stationName=${station.station_name}`}
                                            onClick={(e) => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                                history.push(`/station?stationName=${station.station_name}`)
                                                setIsOpen(false)
                                            }}
                                        >
                                            {station.station_name}
                                        </WemoloTextLink>
                                    </React.Fragment>
                                ))}
                            </>
                        )}
                    </span>
                )

                return {
                    type: 'device' as const,
                    deviceType: device.type,
                    id: device.uuid,
                    name: device.device_name || device.uuid,
                    description: description,
                    searchableDescription: searchableDescription,
                    navigateTo: () => {
                        history.push(`/device?deviceId=${device.uuid}`)
                        setIsOpen(false)
                    }
                }
            }) || []

        return [...lotItems, ...stationItems, ...deviceItems]
    }, [data, allStations, history, setIsOpen])

    // Return both raw data and search term for client-side refinement
    const searchData = useMemo(() => {
        return rawSearchData
    }, [rawSearchData])

    return {
        searchData,
        isLoading: loading
    }
}
